/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { BEM, TTranslateHookFn, useTranslate } from "@adapptio/lib-components";
import { useTreeState } from "../../../../../../ui/components/Tree/useTreeState";
import { ITreeViewProps, TreeView } from "../../../../../../ui/components/Tree/TreeView";
import {
	isModel,
	isSchemaConstArrayModel,
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	isSchemaConstObjectModel,
	TGetBlueprintSchemaModel,
	TGetSchemaConstArrayItemSchema
} from "@adapptio/lib-blueprint";
import { TBlueprintThemeCustomCssSchema, TBlueprintThemeSchema } from "@adapptio/lib-core";
import {
	ITreeNode,
	ITreeNodeAction,
	TREE_NODE_ACTION_STYLE,
	TTreeViewNodeActionClickHandler
} from "../../../../../../ui/components/Tree/TreeViewTypes";
import { ThemeEditSelection } from "../../../../Common/ThemeEditSelection";
import { useStateWithEvent } from "../../../../../../ui/hooks";
import { ThemeModel } from "../../../../Common/ThemeModel";

const SPLIT_NAME_REGEX = /(\d+)$/;

function getCssBlocksTree(
	t: TTranslateHookFn,
	model: TGetBlueprintSchemaModel<TBlueprintThemeCustomCssSchema>,
	{ actions, defaultExpanded }: { actions: ITreeNode["actions"]; defaultExpanded?: boolean }
): ITreeNode[] {
	const items: ITreeNode[] = [];

	for (let i = 0; i < model.items.length; i++) {
		const itemModel = model.items[i];

		const treeNode: ITreeNode = {
			id: String(itemModel.nodeId),
			label: itemModel.props.name.value,
			icon: "mdi/language-css3",
			description: "",
			selectable: true,
			metaData: {
				modelNode: itemModel,
				primaryListModel: model
			}
		};

		items.push(treeNode);
	}

	return [
		{
			id: String(model.nodeId),
			label: "CSS Blocks",
			icon: "mdi/language-css3",
			defaultExpanded: !!defaultExpanded,
			children: items.length > 0 ? items : [],
			actions,
			metaData: {
				primaryListModel: model
			}
		}
	];
}

function getStylesTree(
	t: TTranslateHookFn,
	model: TGetBlueprintSchemaModel<TBlueprintThemeSchema>,
	{ actions, cssBlocksActions }: { actions: ITreeNode["actions"]; cssBlocksActions: ITreeNode["actions"] }
): ITreeNode[] {
	const styleIcon = "mdi/brush";
	const viewProps = model.props.spec.props;
	const commonBlocksModel = viewProps.customCss;
	const stylesModel = viewProps.styles;

	const items: ITreeNode[] = [];

	const commonStyleNode: ITreeNode = {
		id: "common-style",
		label: "common",
		icon: styleIcon,
		selectable: false,
		defaultExpanded: true,
		children: getCssBlocksTree(t, commonBlocksModel, { actions: cssBlocksActions, defaultExpanded: true })
	};

	items.push(commonStyleNode);

	for (let i = 0; i < stylesModel.items.length; i++) {
		const mapEntry = stylesModel.items[i];

		if (!mapEntry?.value) {
			continue;
		}

		const treeNode: ITreeNode = {
			id: String(mapEntry.key.nodeId),
			label: mapEntry.value.props.label.value || mapEntry.key.value,
			icon: styleIcon,
			selectable: true,
			children: getCssBlocksTree(t, mapEntry.value.props.customCss, { actions: cssBlocksActions }),
			metaData: {
				mapModel: stylesModel,
				mapEntry
			}
		};

		items.push(treeNode);
	}

	const rootClass = BEM("edt-module-theme-editor-outline__tree-node-section", null, {
		"is-empty": items.length === 0
	});

	return [
		{
			id: "root",
			label: "Styles",
			icon: "mdi/palette",
			defaultExpanded: true,
			children: items.length > 0 ? items : [],
			className: rootClass.toClassName(),
			actions: actions
		}
	];
}

export interface IThemeEditorOutlineStylesProps {
	className?: string;
	themeModel: ThemeModel;
	selection: ThemeEditSelection;
	selectedNodes: ITreeViewProps["selections"];
	onNodeContextMenu: ITreeViewProps["onNodeContextMenu"];
}

export const ThemeEditorOutlineStyles: React.FC<IThemeEditorOutlineStylesProps> = ({
	className,
	themeModel,
	selection,
	selectedNodes,
	onNodeContextMenu
}) => {
	const baseClass = BEM("edt-theme-editor-outline-styles");

	const t = useTranslate();

	const [ treeState, setTreeState ] = useTreeState({ nodes: { root: { expanded: true } } });

	const addStyle = useCallback<TTreeViewNodeActionClickHandler>(() => {
		const viewProps = themeModel.getModel().props.spec.props;
		const mapModel = viewProps.styles;

		const style = mapModel.schema.addElement(
			mapModel,
			mapModel.schema.getUniqueKey(mapModel, "style", true),
			undefined,
			true
		);

		selection.selectStyle(mapModel, style);
	}, [ themeModel ]);

	const addBlock = useCallback<TTreeViewNodeActionClickHandler>(
		(node) => {
			const customCssModel = node?.metaData?.primaryListModel;

			if (
				!isSchemaConstArrayModel<TGetSchemaConstArrayItemSchema<TBlueprintThemeCustomCssSchema>>(
					customCssModel
				)
			) {
				return;
			}

			const identifierIndex = new Set(customCssModel.items.map((item) => item.props.name.value));

			const lastItem = customCssModel.items[customCssModel.items.length - 1];

			let name = lastItem?.props?.name?.value || "block";

			const [ nameBase, iBase ] = name.split(SPLIT_NAME_REGEX);
			let i = iBase ? parseInt(iBase) + 1 : 1;

			do {
				name = nameBase + i;
				i++;
			} while (identifierIndex.has(name));

			const block = customCssModel.schema.addElement(
				customCssModel,
				{ name, content: null, preprocessor: "NONE" },
				true
			);

			if (!isSchemaConstObjectModel(block)) {
				return;
			}

			selection.selectBlock(customCssModel, block);
		},
		[ themeModel ]
	);

	const stylesActions = useMemo<ITreeNodeAction[]>(
		() => [
			{
				id: "add-style",
				icon: "mdi/plus-circle-outline",
				tooltip: "Add Style",
				style: TREE_NODE_ACTION_STYLE.GHOST,
				hasPrimaryAction: true,
				onClick: addStyle
			}
		],
		[ addStyle ]
	);

	const cssBlocksActions = useMemo<ITreeNodeAction[]>(
		() => [
			{
				id: "add-block",
				icon: "mdi/plus-circle-outline",
				tooltip: "Add Block",
				style: TREE_NODE_ACTION_STYLE.GHOST,
				hasPrimaryAction: true,
				onClick: addBlock
			}
		],
		[ addBlock ]
	);

	const handleSelect = useCallback(
		(node: ITreeNode) => {
			const mapModel = node.metaData?.mapModel;
			const mapEntry = node.metaData?.mapEntry;

			if (isSchemaConstMapModel(mapModel) && isSchemaConstMapModelEntry(mapEntry)) {
				selection.selectStyle(mapModel, mapEntry);
				return;
			}

			const modelNode = node.metaData?.modelNode;
			const primaryListModel = node.metaData?.primaryListModel;

			if (isSchemaConstArrayModel(primaryListModel) && isSchemaConstObjectModel(modelNode)) {
				selection.selectBlock(primaryListModel, modelNode);
				return;
			}
		},
		[ selection ]
	);

	const treeItems = useStateWithEvent(
		themeModel.onModelChange,
		() => getStylesTree(t, themeModel.getModel(), { cssBlocksActions, actions: stylesActions }),
		[ stylesActions, cssBlocksActions ]
	);

	const handleNodeDragDrop = useCallback<ITreeViewProps["onNodeDragDrop"]>(
		({ draggedNode, dropTargetNode }) => {
			const itemModel = draggedNode.metaData?.modelNode;
			const targetModel = dropTargetNode.metaData?.modelNode;

			const listModel = isModel(itemModel)
				? itemModel.parent
				: null;

			if (isSchemaConstArrayModel(listModel) && isModel(itemModel)) {
				const currentIndex = listModel.items.findIndex(
					(item) => item === itemModel
				);

				const targetNodeIndex = targetModel && isModel(targetModel)
					? listModel.items.findIndex((item) => item === targetModel)
					: null;

				// Compensate for removal if moving upwards	
				const newIndex = targetNodeIndex !== null
					? (targetNodeIndex > currentIndex ? targetNodeIndex : targetNodeIndex + 1)
					: 0;

				listModel.schema.moveElementByModel(
					listModel,
					itemModel,
					newIndex,
					true
				);

				return;
			}
		},
		[]
	);

	const isNodeDraggable = useCallback((node: ITreeNode) => {
		return isSchemaConstArrayModel(node?.metaData?.primaryListModel) && isModel(node.metaData?.modelNode);
	}, []);

	const isNodeDropAllowed = useCallback<ITreeViewProps["allowDrop"]>(({ draggedNode, dropTargetNode }) => {
		return (
			isSchemaConstArrayModel(dropTargetNode?.metaData?.primaryListModel) &&
			isSchemaConstArrayModel(draggedNode?.metaData?.primaryListModel) &&
			dropTargetNode.metaData.primaryListModel.nodeId === draggedNode.metaData.primaryListModel.nodeId
		);
	}, []);

	if (className) {
		baseClass.add(className);
	}

	return (
		<TreeView
			onStateUpdate={setTreeState}
			state={treeState}
			items={treeItems}
			onNodeSelect={handleSelect}
			selections={selectedNodes}
			onNodeContextMenu={onNodeContextMenu}
			onNodeDragDrop={handleNodeDragDrop}
			isDraggable={isNodeDraggable}
			allowDrop={isNodeDropAllowed}
		/>
	);
};
